import { LoginPage, LoginResultComplete } from '@pro4-erp/core-web';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, createTheme } from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@emotion/react';
import { useAuthStore, User, UserImplementation } from '@/core';

interface LoginPageProps {
  userImpl: UserImplementation;
}

const Customlayout = ({ children }: React.PropsWithChildren) => {
  const { t } = useTranslation();
  const theme = createTheme({
    palette: {
      primary: {
        main: '#ffffffed',
      },
      text: {
        primary: '#ffffffed',
      },
    },
    components: {
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: '#ffffff', // Ensure label text is always white
            zIndex: 2,
            '&.Mui-focused': {
              color: '#90caf9', // Change label color when focused
              zIndex: 2,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            position: 'relative',
            zIndex: 1,
            backgroundColor: 'rgba(30, 30, 30, 0.5)', // Dark background for input field
            color: '#ffffff', // White text inside input
            '& .MuiOutlinedInput-notchedOutline': {
              backgroundColor: 'rgba(30, 30, 30, 0.5)',
              borderColor: '#bbbbbb', // Light gray border
              WebkitTextFillColor: '#ffffff !important',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              backgroundColor: 'rgba(30, 30, 30, 0.5)',
              borderColor: '#ffffff', // White on hover
              WebkitTextFillColor: '#ffffff !important',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              backgroundColor: 'rgba(30, 30, 30, 0.5)',
              borderColor: '#90caf9', // Light blue when focused
              color: '#ffffff',
              WebkitTextFillColor: '#ffffff !important',
            },
          },
          input: {
            color: '#ffffff !important', // White text inside input
            caretColor: '#90caf9',
            position: 'relative',
            zIndex: 2,
            '&:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0px 5px rgba(30, 30, 30, 0.5) inset', // 🔥 Fixes the background
              WebkitTextFillColor: '#ffffff !important', // ✅ Forces white text
              caretColor: '#90caf9', // Optional: Cursor color
              transition: 'background-color 5000s ease-in-out 0s',
              color: '#ffffff',
              '&:focus': {
                color: '#ffffff !important', // ✅ Ensures text remains white when focused
                WebkitTextFillColor: '#ffffff !important',
              },
            },
          },
        },
      },

      MuiTypography: {
        styleOverrides: {
          root: {
            color: '#ffffffed',
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: '#ffffffed',
          },
        },
      },
    },
  });
  return (
    <ThemeProvider theme={theme}>
      <Box
        component="main"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100vh',
          backgroundImage: 'url(/img/background.png)',
          'background-repeat': 'no-repeat',
          'background-size': 'cover',
        }}
      >
        {/* main container */}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            // width: '100%',
          }}
        >
          {/* lock avatar */}
          <Box sx={{ m: 2, mb: '2rem' }}>
            <img src="/img/SIQ_logo.png" alt="logo" style={{ width: '9.625rem' }} />
          </Box>
          {/* title text */}
          <Typography component="h1" variant="h4">
            {t('Welcome back to the Management Systems portal', {
              defaultValue: 'Welcome back to the Management Systems portal',
            })}
          </Typography>
          {/* form container */}
          <Box
            sx={{
              width: '75%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mt: '1rem',
            }}
          >
            {children}
          </Box>
        </Box>
        {/* <Box
          sx={{
            paddingLeft: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            maxHeight: '100vh',
          }}
        >
          <img src="/img/background.png" alt="bg" style={{ maxHeight: '100vh' }} />
        </Box> */}
      </Box>
    </ThemeProvider>
  );
};

const LoginPageWrapper = ({ userImpl }: LoginPageProps) => {
  const navigate = useNavigate();

  // When the LoginPage requests a redirect to forgot-password
  const handleGoToForgotPassword = () => {
    navigate('/auth/forgot-password');
  };
  const authStore = useAuthStore();

  useEffect(() => {
    if (authStore.getIsAuthenticated()) {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStore]);

  return (
    <LoginPage<User>
      onGoToForgotPassword={handleGoToForgotPassword}
      authenticateUser={authStore.authenticateUser}
      loginCallback={userImpl.login}
      loginFormIdentifierField="email"
      customLayout={Customlayout}
      twoFactorCallback={async (credentials: {
        identifier: string;
        password: string;
        code: string;
      }) => {
        let temp = {};
        await setTimeout(() => {
          temp = { ...credentials };
        }, 5);
        return temp as LoginResultComplete<User>;
      }}
    />
  );
};

export default LoginPageWrapper;
